import styled from '@emotion/styled'
import { Button } from 'app/components/Common/Button'
import { Image, Props as ImageProps } from 'app/components/Common/Image'
import { useVocabularyData } from 'app/utils/vocabulary'
import React, { memo } from 'react'
import { LazyLoadComponent } from 'react-lazy-load-image-component'

export interface Props {
  description?: string
  image?: ImageProps
  languageCode: string
  title?: string
  website?: string
}

export const Visual = memo(function Visual({
  description,
  image,
  languageCode,
  title,
  website,
}: Props) {
  return (
    <Container>
      <Background>
        {image ? (
          <LazyLoadComponent>
            <Image media="(min-width: 2800px)" {...image} />
          </LazyLoadComponent>
        ) : null}
      </Background>

      <Wrapper>
        {title ? <Title>{title}</Title> : null}
        {description ? (
          <Description dangerouslySetInnerHTML={{ __html: description }} />
        ) : null}
        {website ? (
          <CTA
            variant="simple"
            label={useVocabularyData('visit-website', languageCode)}
            rel="noopener"
            target="_blank"
            URL={website}
          />
        ) : null}
      </Wrapper>
    </Container>
  )
})

const Container = styled.div`
  margin-top: 7.5rem;
`

const Background = styled.div`
  height: 66.7vh;
  max-height: 37.5rem;
  background: ${({ theme }) => theme.colors.variants.neutralLight3};
  position: relative;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    position: absolute;
    top: 0;
    left: 0;
  }
`

const Wrapper = styled.div`
  width: 60.556vw;
  max-width: 54.5rem;
  background: ${({ theme }) => theme.colors.variants.neutralLight4};
  margin: -5.3125rem 1.875rem 0 7.639vw;
  padding: 3.75rem;
  position: relative;
  &:before {
    content: '';
    display: block;
    height: 0.0625rem;
    background: ${({ theme }) => theme.colors.variants.neutralLight4};
    position: absolute;
    top: -1.3125rem;
    right: 0;
    left: 3.75rem;
  }
`

const Title = styled.h3`
  color: ${({ theme }) => theme.colors.variants.secondaryLight};
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-size: 1.5rem;
  letter-spacing: 0.075rem;
  line-height: 2.5rem;
  text-transform: uppercase;
`

const Description = styled.div`
  color: ${({ theme }) => theme.colors.variants.neutralDark4};
  font-family: ${({ theme }) => theme.fontFamily.paragraph};
  font-size: 1rem;
  line-height: 2rem;
  margin-top: 1.25rem;
`

const CTA = styled(Button)`
  margin-top: 2.5rem;
`
